<template>
    <div ref="masonry" class="masonry-container">
        <div
            v-for="(item, index) in items"
            :key="index"
            class="masonry-item"
            :style="itemStyles[index]"
        >
            <slot :item="item" :index="index" />
        </div>
    </div>
</template>

<script>
    export default {
        name: 'MasonryGallery',

        props: {
            items: {
                type: Array,
                required: true,
            },
            columnGutter: {
                type: Number,
                default: 32,
            },
            breakpoints: {
                type: Object,
                default: () => ({
                    default: 1,
                    640: 2,
                    768: 3,
                }),
            },
        },

        data() {
            return {
                columns: 1,
                itemStyles: [],
                columnHeights: [],
                resizeObserver: null,
            };
        },

        watch: {
            items: {
                handler() {
                    this.$nextTick(() => {
                        this.waitForImages().then(() => {
                            this.layoutMasonry();
                        });
                    });
                },
                deep: true,
            },
        },

        mounted() {
            this.calculateColumns();
            window.addEventListener('resize', this.onResize);

            // Wait for images to load then layout
            this.$nextTick(() => {
                this.waitForImages().then(() => {
                    this.layoutMasonry();
                });
            });

            // Set up ResizeObserver for dynamic content
            if (typeof ResizeObserver !== 'undefined') {
                this.resizeObserver = new ResizeObserver(() => {
                    this.layoutMasonry();
                });
                this.resizeObserver.observe(this.$refs.masonry);
            }
        },

        beforeUnmount() {
            window.removeEventListener('resize', this.onResize);
            if (this.resizeObserver) {
                this.resizeObserver.disconnect();
            }
        },

        methods: {
            calculateColumns() {
                const width = window.innerWidth;
                const breakpointKeys = Object.keys(this.breakpoints)
                    .map(Number)
                    .filter((key) => !isNaN(key))
                    .sort((a, b) => b - a);

                for (const breakpoint of breakpointKeys) {
                    if (width >= breakpoint) {
                        this.columns = this.breakpoints[breakpoint];
                        return;
                    }
                }

                this.columns = this.breakpoints.default || 1;
            },

            waitForImages() {
                const images = this.$refs.masonry.querySelectorAll('img');
                const promises = Array.from(images).map((img) => {
                    if (img.complete) {
                        return Promise.resolve();
                    }
                    return new Promise((resolve) => {
                        img.addEventListener('load', resolve);
                        img.addEventListener('error', resolve);
                    });
                });
                return Promise.all(promises);
            },

            layoutMasonry() {
                if (!this.$refs.masonry) { return; }

                const container = this.$refs.masonry;
                const items = container.querySelectorAll('.masonry-item');

                if (items.length === 0) { return; }

                // Initialize column heights
                this.columnHeights = new Array(this.columns).fill(0);
                const newItemStyles = [];

                // Get container width
                const containerWidth = container.offsetWidth;
                const columnWidth = (containerWidth - (this.columnGutter * (this.columns - 1))) / this.columns;

                items.forEach((item, index) => {
                    // Find shortest column
                    const shortestColumnIndex = this.columnHeights.indexOf(Math.min(...this.columnHeights));

                    // Calculate position
                    const left = shortestColumnIndex * (columnWidth + this.columnGutter);
                    const top = this.columnHeights[shortestColumnIndex];

                    // Set item style
                    newItemStyles[index] = {
                        position: 'absolute',
                        left: `${left}px`,
                        top: `${top}px`,
                        width: `${columnWidth}px`,
                        transition: 'all 0.3s ease',
                    };

                    // Update column height
                    this.columnHeights[shortestColumnIndex] += item.offsetHeight + this.columnGutter;
                });

                this.itemStyles = newItemStyles;

                // Set container height
                const maxHeight = Math.max(...this.columnHeights);
                container.style.height = `${maxHeight}px`;
            },

            onResize() {
                const oldColumns = this.columns;
                this.calculateColumns();

                if (oldColumns !== this.columns) {
                    this.layoutMasonry();
                }
            },
        },
    };
</script>

<style scoped>
.masonry-container {
    position: relative;
    width: 100%;
}

.masonry-item {
    box-sizing: border-box;
}
</style>
